import React from 'react';
import {AdminTriState} from '../../domain/adminWordlists';
import {SearchIcon, XIcon} from '../Icons';

export interface AdminWordlistFilterValues {
  search: string;
  isOfficial: AdminTriState;
  isPublic: AdminTriState;
  includeDeleted: boolean;
}

interface AdminWordlistFiltersProps {
  values: AdminWordlistFilterValues;
  /** The uncommitted search box text — the container debounces it into `values.search`. */
  searchInput: string;
  onSearchInput: (value: string) => void;
  onChange: (patch: Partial<AdminWordlistFilterValues>) => void;
  onClear: () => void;
  resultCount: number;
  isLoading: boolean;
}

const selectClass =
  'rounded-lg border border-gray-800 bg-gray-950/60 px-2.5 py-1.5 text-xs text-gray-200 '
  + 'focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';

/**
 * Search plus the three filters the endpoint takes: official, public, and whether soft-deleted
 * lists are shown at all. `includeDeleted` defaults off, matching the endpoint's own default — a
 * list an owner deleted shouldn't turn up in a catalog search unless asked for.
 */
const AdminWordlistFilters: React.FC<AdminWordlistFiltersProps> = ({
  values,
  searchInput,
  onSearchInput,
  onChange,
  onClear,
  resultCount,
  isLoading,
}) => {
  const hasFilters = Boolean(
    values.search || values.isOfficial || values.isPublic || values.includeDeleted
  );

  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative">
        <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-600" />
        <input
          type="search"
          value={searchInput}
          onChange={event => onSearchInput(event.target.value)}
          placeholder="Search by name…"
          aria-label="Search word lists by name"
          className="w-60 rounded-lg border border-gray-800 bg-gray-950/60 py-1.5 pl-8 pr-7 text-xs
           text-gray-200 placeholder-gray-600 focus:border-indigo-500 focus:outline-none
           focus:ring-1 focus:ring-indigo-500 [&::-webkit-search-cancel-button]:hidden"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => onSearchInput('')}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-300 cursor-pointer"
          >
            <XIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <select
        value={values.isOfficial}
        onChange={event => onChange({isOfficial: event.target.value as AdminTriState})}
        aria-label="Official"
        className={selectClass}
      >
        <option value="">Any list</option>
        <option value="true">Official only</option>
        <option value="false">Non-official only</option>
      </select>

      <select
        value={values.isPublic}
        onChange={event => onChange({isPublic: event.target.value as AdminTriState})}
        aria-label="Visibility"
        className={selectClass}
      >
        <option value="">Any visibility</option>
        <option value="true">Public only</option>
        <option value="false">Private only</option>
      </select>

      <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border
       border-gray-800 bg-gray-950/60 px-2.5 py-1.5 text-xs text-gray-300">
        <input
          type="checkbox"
          checked={values.includeDeleted}
          onChange={event => onChange({includeDeleted: event.target.checked})}
          className="h-3 w-3 accent-indigo-500"
        />
        Show deleted
      </label>

      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-indigo-400 hover:text-indigo-300 cursor-pointer"
        >
          Clear
        </button>
      )}

      <span className="ml-auto text-[11px] tabular-nums text-gray-600">
        {isLoading ? 'Loading…' : `${resultCount.toLocaleString()} matching`}
      </span>
    </div>
  );
};

export default AdminWordlistFilters;
